import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { supabase, friendlyError } from "../supabase.js";
import { useLiveTables, useFallbackPoll } from "../useLive.js";
import { computeSchedule, rollUp } from "./cpm.js";
import { impactOf } from "./impact.js";

/* The schedule, live, and editable while a meeting is happening.
 *
 * Everything derived is derived here and now: early and late dates, float,
 * the critical path, and the summary bars. None of it is stored, so none of
 * it can be stale, and 555 activities resolve in under a tenth of a second,
 * which is less time than the round trip that fetched them.
 *
 * Two people can work the same plan at once. Changes arrive over the same
 * live subscription every register uses, so an update made on somebody else's
 * machine appears here without a refresh. That is the whole reason a schedule
 * belongs in a database rather than in a file being emailed around.
 */

const TABLES = ["schedule_stages", "schedule_links", "schedule_baselines"];

export function useSchedule(contractId) {
  const [stages, setStages] = useState(null);
  const [links, setLinks] = useState([]);
  /* The plan as it was when somebody froze it, and the rows behind it. Kept
     beside the live plan so every screen can show movement without another
     round trip. */
  const [baseline, setBaseline] = useState(null);
  const [baselineRows, setBaselineRows] = useState([]);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const nudge = useRef(null);
  useEffect(() => () => clearTimeout(nudge.current), []);

  const load = useCallback(async () => {
    if (!contractId) { setStages([]); setLinks([]); return; }
    const [s, l] = await Promise.all([
      supabase.from("schedule_stages").select("*")
        .eq("contract_id", contractId).is("deleted_at", null)
        .order("activity_id", { ascending: true, nullsFirst: false }),
      supabase.from("schedule_links").select("*")
        .eq("contract_id", contractId).is("deleted_at", null),
    ]);
    if (s.error || l.error) { setError(friendlyError(s.error || l.error)); return; }

    /* Only the one everything is measured against. Others are kept and are a
       later screen's problem. */
    const { data: current } = await supabase
      .from("schedule_baselines")
      .select("id, title, taken_at")
      .eq("contract_id", contractId).eq("is_current", true)
      .is("deleted_at", null).maybeSingle();

    setBaseline(current ?? null);
    if (current) {
      const { data: was } = await supabase
        .from("schedule_baseline_rows")
        .select("activity_code, title, start_date, finish_date, percent_complete")
        .eq("baseline_id", current.id);
      setBaselineRows(was ?? []);
    } else {
      setBaselineRows([]);
    }

    setStages(s.data ?? []);
    setLinks((l.data ?? []).map((r) => ({
      id: r.id, from: r.predecessor_id, to: r.successor_id,
      link_type: r.link_type, lag_days: r.lag_days,
    })));
    setError("");
  }, [contractId]);

  useEffect(() => { setStages(null); load(); }, [load]);

  const live = useLiveTables(TABLES, contractId ? `contract_id=eq.${contractId}` : null,
    useCallback(() => {
      clearTimeout(nudge.current);
      nudge.current = setTimeout(load, 250);
    }, [load]));
  useFallbackPoll(load, live);

  /* The whole calculation, redone whenever a row changes. Cheap enough to do
     on every keystroke, which is what makes an impact preview possible at
     all. */
  /* A summary is a row with something under it. Nothing in the database says
     so, and nothing should: it is a fact about the shape of the plan, and a
     stored flag would go wrong the first time an activity is reparented. */
  const shaped = useMemo(() => {
    if (!stages) return null;
    const parents = new Set(stages.map((s) => s.parent_id).filter(Boolean));
    return stages.map((s) => ({ ...s, is_summary: parents.has(s.id) }));
  }, [stages]);

  const computed = useMemo(() => {
    if (!shaped) return null;
    const result = computeSchedule(shaped, links);
    return { ...result, activities: rollUp(mergeBack(shaped, result.activities)) };
  }, [shaped, links]);

  /* What a change would do, without saving it. */
  const preview = useCallback(
    (changes) => (shaped ? impactOf(shaped, links, changes) : null),
    [shaped, links],
  );

  const saveActivity = useCallback(async (row) => {
    setBusy(true);
    const payload = { ...row };
    for (const key of ["created_at", "updated_at", "added_by", "updated_by", "activity_code"]) {
      delete payload[key];
    }
    /* Everything the engine worked out is stripped before saving. Writing a
       computed float back into the row is how a derived number becomes a
       stored one that was true once. */
    for (const key of ["early_start", "early_finish", "late_start", "late_finish",
                       "total_float", "is_critical", "is_summary"]) {
      delete payload[key];
    }
    const { error: failed } = payload.id
      ? await supabase.from("schedule_stages").update(payload).eq("id", payload.id)
      : await supabase.from("schedule_stages").insert({ ...payload, contract_id: contractId });
    setBusy(false);
    if (failed) { setError(friendlyError(failed)); return false; }
    await load();
    return true;
  }, [contractId, load]);

  const removeActivity = useCallback(async (row) => {
    const { error: failed } = await supabase.from("schedule_stages")
      .update({ deleted_at: new Date().toISOString() }).eq("id", row.id);
    if (failed) { setError(friendlyError(failed)); return false; }
    await load();
    return true;
  }, [load]);

  const addLink = useCallback(async (link) => {
    const { error: failed } = await supabase.from("schedule_links").insert({
      contract_id: contractId,
      predecessor_id: link.from,
      successor_id: link.to,
      link_type: link.link_type ?? "FS",
      lag_days: link.lag_days ?? 0,
    });
    if (failed) { setError(friendlyError(failed)); return false; }
    await load();
    return true;
  }, [contractId, load]);

  /* Several at once, for tying a sequence. A plan with 300 loose activities
     is not fixed one link at a time, and the work comes in chains: onboarding,
     training, installs, optimization, turnover, three islands over. */
  const addLinks = useCallback(async (list) => {
    if (!list?.length) return true;
    const { error: failed } = await supabase.from("schedule_links").insert(
      list.map((l) => ({
        contract_id: contractId,
        predecessor_id: l.from,
        successor_id: l.to,
        link_type: l.link_type ?? "FS",
        lag_days: l.lag_days ?? 0,
      })),
    );
    if (failed) { setError(friendlyError(failed)); return false; }
    await load();
    return true;
  }, [contractId, load]);

  const removeLink = useCallback(async (id) => {
    const { error: failed } = await supabase.from("schedule_links").delete().eq("id", id);
    if (failed) { setError(friendlyError(failed)); return false; }
    await load();
    return true;
  }, [load]);

  return {
    stages, links, computed, error, busy, live, baseline, baselineRows,
    reload: load, preview, saveActivity, removeActivity, addLink, addLinks, removeLink,
  };
}

/* The engine works on activities and leaves summaries out. This puts the
   summaries back so the page has one list to draw, in the order they were
   read, rather than two that have to be zipped together at every use. */
function mergeBack(stages, scheduled) {
  const by = new Map(scheduled.map((a) => [a.id, a]));
  return stages.map((s) => by.get(s.id) ?? { ...s, is_summary: true });
}
